import { BookOpen, Link2, CheckCircle } from 'lucide-react';

interface CauseChain {
  immediate_cause: string;
  root_cause: string;
  consequence: string;
}

interface ActionSplit {
  fix: string;
  prevent: string;
  escalate: string;
}

interface LayeredRca {
  story: string;
  defect_class: string;
  cause_chain: CauseChain;
  evidence: Array<{ claim: string; evidence: string }>;
  action_split: ActionSplit;
}

interface LayeredRcaCardsProps {
  layeredRca: LayeredRca | null;
}

export function LayeredRcaCards({ layeredRca }: LayeredRcaCardsProps) {
  if (!layeredRca) {
    return null;
  }

  return (
    <div className="space-y-4 mb-6">
      {/* Layer 1: Story */}
      <div className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50">
        <div className="px-4 py-3 bg-blue-100 flex items-center gap-2">
          <BookOpen size={16} className="text-blue-600" />
          <span className="text-sm font-semibold text-blue-900">The Story (Plain Language)</span>
        </div>
        <div className="px-4 py-3 text-sm text-gray-800 leading-relaxed">
          {layeredRca.story}
        </div>
      </div>

      {/* Layer 3: Cause Chain */}
      <div className="border border-amber-200 rounded-lg overflow-hidden bg-amber-50">
        <div className="px-4 py-3 bg-amber-100 flex items-center gap-2">
          <Link2 size={16} className="text-amber-600" />
          <span className="text-xs font-semibold text-amber-900">The Cause Chain (Why It Happened)</span>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div>
            <div className="text-[10px] font-semibold text-amber-700 mb-1">1. Immediate Cause</div>
            <div className="text-xs text-gray-800">{layeredRca.cause_chain.immediate_cause}</div>
          </div>
          <div className="border-t border-amber-200 pt-3">
            <div className="text-[10px] font-semibold text-amber-700 mb-1">2. Root Cause</div>
            <div className="text-xs text-gray-800">{layeredRca.cause_chain.root_cause}</div>
          </div>
          <div className="border-t border-amber-200 pt-3">
            <div className="text-[10px] font-semibold text-amber-700 mb-1">3. Consequence</div>
            <div className="text-xs text-gray-800">{layeredRca.cause_chain.consequence}</div>
          </div>
        </div>
      </div>

      {/* Layer 5: Action Split */}
      <div className="border border-green-200 rounded-lg overflow-hidden bg-green-50">
        <div className="px-4 py-3 bg-green-100 flex items-center gap-2">
          <CheckCircle size={16} className="text-green-600" />
          <span className="text-xs font-semibold text-green-900">The Action Plan (Who Does What)</span>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div>
            <div className="text-[10px] font-semibold text-green-700 mb-1">Fix</div>
            <div className="text-xs text-gray-800">{layeredRca.action_split.fix}</div>
          </div>
          <div className="border-t border-green-200 pt-3">
            <div className="text-[10px] font-semibold text-green-700 mb-1">Prevent</div>
            <div className="text-xs text-gray-800">{layeredRca.action_split.prevent}</div>
          </div>
          <div className="border-t border-green-200 pt-3">
            <div className="text-[10px] font-semibold text-green-700 mb-1">Escalate</div>
            <div className="text-xs text-gray-800">{layeredRca.action_split.escalate}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
